import { useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, Bug, Lightbulb, Question, X, PaperPlaneRight } from "@phosphor-icons/react";
import client from "../lib/api";

// Floating pilot feedback widget — visible on all authenticated /app/* pages.
// Auto-captures the current page URL so admins know where the report came from.
// Sits ABOVE the AI Copilot button (bottom-24 right-6) to avoid collision.
export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Add a few words about what you'd like to share.");
      return;
    }
    setSending(true);
    try {
      await client.post("/feedback", {
        feedback_type: type,
        message: message.trim(),
        page_url: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success("Feedback sent. We read every one — thank you.");
      setOpen(false);
      setMessage("");
      setType("general");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send feedback — try again.");
    } finally {
      setSending(false);
    }
  };

  const TYPES = [
    { key: "bug", label: "Bug", icon: Bug, color: "rose" },
    { key: "idea", label: "Idea", icon: Lightbulb, color: "amber" },
    { key: "general", label: "General", icon: Question, color: "sky" },
  ];
  const TYPE_STYLES = {
    rose: {
      active: "border-rose-500 bg-rose-500/15 text-rose-200",
      idle: "border-white/10 hover:border-rose-500/40 text-neutral-400",
    },
    amber: {
      active: "border-amber-500 bg-amber-500/15 text-amber-200",
      idle: "border-white/10 hover:border-amber-500/40 text-neutral-400",
    },
    sky: {
      active: "border-sky-500 bg-sky-500/15 text-sky-200",
      idle: "border-white/10 hover:border-sky-500/40 text-neutral-400",
    },
  };

  return (
    <>
      {/* Floating trigger — sits above the AI Copilot button */}
      <button
        type="button"
        data-testid="feedback-widget-btn"
        onClick={() => setOpen(true)}
        title="Send feedback"
        aria-label="Send feedback"
        className="fixed bottom-24 right-6 w-12 h-12 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] flex items-center justify-center shadow-lg z-30 transition-all hover:scale-105"
      >
        <ChatCircleDots size={20} weight="fill" className="text-white" />
      </button>

      {/* Modal */}
      {open && (
        <div
          data-testid="feedback-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <div className="overline text-[#FF9764] mb-0.5">Send feedback</div>
                <div className="text-sm font-medium text-white">What's on your mind?</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="feedback-close-btn"
                className="text-neutral-500 hover:text-white p-1 rounded-md"
                aria-label="Close feedback"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submit} className="p-5 space-y-4">
              {/* Type pills */}
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Type</div>
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map((t) => {
                    const styles = TYPE_STYLES[t.color];
                    const active = type === t.key;
                    return (
                      <button
                        type="button"
                        key={t.key}
                        onClick={() => setType(t.key)}
                        data-testid={`feedback-type-${t.key}`}
                        aria-pressed={active}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-md border transition-colors ${active ? styles.active : styles.idle}`}
                      >
                        <t.icon size={18} weight={active ? "fill" : "regular"} />
                        <span className="text-xs font-medium">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Message</div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="feedback-message"
                  rows={5}
                  placeholder={
                    type === "bug"
                      ? "What happened, what you expected, and where you saw it..."
                      : type === "idea"
                      ? "What would make FreightPilotAI better for you?"
                      : "Tell us what's on your mind..."
                  }
                  className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors resize-none placeholder:text-neutral-600"
                  maxLength={4000}
                />
                <div className="text-[10px] text-neutral-600 mt-1 text-right font-mono">
                  {message.length} / 4000
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-1">
                <div className="text-[11px] text-neutral-500 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Sending from{" "}
                  <span className="font-mono text-neutral-400">
                    {typeof window !== "undefined" ? window.location.pathname : "/app"}
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={sending || message.trim().length < 3}
                  data-testid="feedback-submit-btn"
                  className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending…" : "Send"}
                  <PaperPlaneRight size={14} weight="bold" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
